import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'antd';

/**
 * CurrencyInput - A controlled input for currency values with localization and formatting.
 *
 * Usage Example:
 *
 * // In your parent component:
 * import React, { useState } from 'react';
 * import CurrencyInput from '@/components/CurrencyInput';
 *
 * function Example() {
 *   const [amount, setAmount] = useState(0);
 *
 *   return (
 *     <>
 *       // With currency symbol (default)
 *       <CurrencyInput value={amount} onChange={setAmount} />
 *
 *       // Without currency symbol
 *       <CurrencyInput value={amount} onChange={setAmount} showSymbol={false} />
 *
 *       // With prefill and highlight
 *       <CurrencyInput value={amount} onChange={setAmount} prefillValue={1000} prefillColor="#52c41a" />
 *     </>
 *   );
 * }
 *
 * // Props:
 * // - value: string | number (the current value)
 * // - onChange: function (called with the numeric value on change)
 * // - locale: string (default 'en-US')
 * // - currency: string (default 'THB')
 * // - showSymbol: boolean (default true, if false, show only number)
 * // - initialColor: string (optional, sets initial input color until user modifies)
 * // - ...rest: other input props
 */
const CurrencyInput = ({ value, onChange, locale = 'en-US', currency = 'THB', showSymbol = true, initialColor, ...rest }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isPristine, setIsPristine] = useState(true);
  const inputRef = useRef(null);

  // Format number as currency string or decimal string
  function formatCurrency(val, locale, currency, showSymbol) {
    if (val === '' || val === null || val === undefined || isNaN(val)) return '';
    const number = Number(val);
    if (showSymbol) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(number);
    } else {
      return new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(number);
    }
  }

  // Format as plain number string (no grouping, no symbol)
  function formatPlainNumber(val) {
    if (val === '' || val === null || val === undefined || isNaN(val)) return '';
    const number = Number(val);
    return number.toFixed(2);
  }

  // Remove all non-numeric except dot and minus
  function parseCurrency(str) {
    if (!str) return '';
    // Remove currency symbols and grouping
    const cleaned = str.replace(/[^\d.-]/g, '');
    return cleaned;
  }

  // Handle input change
  const handleChange = (e) => {
    const input = e.target.value;
    setInputValue(input);
    if (isPristine) setIsPristine(false);
    if (onChange) {
      const numeric = parseCurrency(input);
      onChange(numeric === '' ? '' : Number(numeric));
    }
  };

  // On focus, show plain number
  const handleFocus = () => {
    setIsEditing(true);
    if (value === '' || value === null || value === undefined || isNaN(value)) {
      setInputValue('');
    } else {
      setInputValue(formatPlainNumber(value));
    }
    // Optionally select all text
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  // On blur, commit the latest value and show formatted value
  const handleBlur = () => {
    setIsEditing(false);
    setInputValue('');
    if (onChange) {
      const numeric = parseCurrency(inputValue);
      if (numeric === '') {
        onChange('');
      } else if (Number(numeric) !== Number(value)) {
        onChange(Number(numeric));
      }
    }
  };

  // Keep inputValue in sync with value prop when not editing
  React.useEffect(() => {
    if (!isEditing) {
      setInputValue('');
    }
  }, [value, isEditing]);

  // Compute style for input
  const inputStyle = (initialColor && isPristine) ? { color: initialColor } : undefined;

  // Decide what to display
  const display = isEditing
    ? inputValue
    : (value === '' || value === null || value === undefined || isNaN(value))
      ? ''
      : formatCurrency(value, locale, currency, showSymbol);

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      style={inputStyle}
      {...rest}
    />
  );
};

CurrencyInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  locale: PropTypes.string,
  currency: PropTypes.string,
  showSymbol: PropTypes.bool,
  initialColor: PropTypes.string,
};

export default CurrencyInput;
